import React, { Component, ComponentType } from 'react'
import { Box, BoxProps, Collapse, InputAdornment, styled, TextField, TextFieldProps } from '@mui/material'
import { IFormInputBase } from './types'
import { getErrorMessage } from './helpers'
import { IRichTooltipPanelConfig, RichTooltip, HelpOutlinePulseIcon } from '../components/rich-tooltip'

export interface IInputImageFileSlots {
  wrapProps?: BoxProps
  inputProps?: TextFieldProps
}

export interface IInputImageFileProps<T> extends IFormInputBase<T, IInputImageFileSlots> {
  tooltip?: IRichTooltipPanelConfig
}

export interface IInputImageFileState {
  imageUrl?: string
}

export interface IInputImageFileParams<T> {
  imageGetter?: (data?: Partial<T>) => string | undefined
  tooltip?: IRichTooltipPanelConfig
}

function createInputImageFile<T>(params?: IInputImageFileParams<T>): ComponentType<IInputImageFileProps<T>> {
  class InputImageFile extends Component<IInputImageFileProps<T>, IInputImageFileState> {
    constructor(props: IInputImageFileProps<T>) {
      super(props)
      this.state = {}
    }

    private get slots() {
      return {
        tooltip: this.props.tooltip || params?.tooltip
      }
    }

    render() {
      const url = this.getImageUrl()
      return (
        <Wrap {...this.props.slots?.wrapProps}>
          <Box sx={{ flex: 1 }}>
            <TextField variant='outlined' type='file' fullWidth inputProps={{ accept: 'image/*', multiple: false }} {...this.getTextFieldProps()} />
          </Box>
          <Collapse in={!!url} orientation='horizontal' unmountOnExit>
            <ImageWrap>
              <img src={url} alt='berlintomek-image' />
            </ImageWrap>
          </Collapse>
        </Wrap>
      )
    }

    getTextFieldProps = (): TextFieldProps => {
      const { name, messageErrors, slots, onBlur } = this.props
      const errorMessage = getErrorMessage(messageErrors, name)
      const tfp: TextFieldProps = {
        name: name?.toString(),
        error: errorMessage.error,
        helperText: errorMessage.message,
        ...slots?.inputProps,
        onBlur: () => {
          if (!name || !onBlur) return
          onBlur(name)
        },
        onChange: this.handleChange
      }
      if (!tfp.InputProps) tfp.InputProps = {}
      if (this.slots.tooltip) {
        tfp.InputProps.endAdornment = (
          <InputAdornment position='end'>
            {this.slots.tooltip && (
              <RichTooltip panel={this.slots.tooltip}>
                <HelpOutlinePulseIcon fontSize='small' />
              </RichTooltip>
            )}
          </InputAdornment>
        )
      }
      return tfp
    }

    handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      const file = event.target.files?.[0]
      if (file) {
        const imageUrl = URL.createObjectURL(file)
        this.setState({ imageUrl }, () => {
          if (!this.props.name) return
          this.props.onBlur && this.props.onBlur(this.props.name)
        })
      }
    }

    getImageUrl = () => {
      const { data } = this.props
      const value = params?.imageGetter ? params.imageGetter(data) : undefined
      return this.state.imageUrl || value || ''
    }
  }
  return InputImageFile
}
export default createInputImageFile

const Wrap = styled(Box)({
  display: 'flex',
  gap: '9px',
  alignItems: 'start'
})

const ImageWrap = styled(Box)({
  height: '56px',
  width: '56px',
  marginRight: '6px',
  borderRadius: '4px',
  overflow: 'hidden',
  border: '1px solid rgba(145, 158, 171, .16)',
  '& > img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
})
